import {
  useCallback, useEffect, useRef, useState,
} from 'react'

export type VoiceRecorderStatus = 'idle' | 'requesting' | 'recording' | 'processing' | 'error'

type Options = {
  maxDurationSeconds?: number
  onRecorded: (audio: Blob) => Promise<void>
}

const pickMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return ''
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']

  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || ''
}

export const isVoiceRecordingSupported = () => typeof window !== 'undefined'
  && typeof MediaRecorder !== 'undefined'
  && !!navigator.mediaDevices?.getUserMedia

// enregistre la voix du prospect avec MediaRecorder, puis confie le blob à `onRecorded` (transcription serveur)
export const useVoiceRecorder = ({ maxDurationSeconds = 60, onRecorded }: Options) => {
  const [status, setStatus] = useState<VoiceRecorderStatus>('idle')
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<number | null>(null)
  const startedAtRef = useRef(0)

  const clearTimer = () => {
    if (timerRef.current) window.clearInterval(timerRef.current)
    timerRef.current = null
  }

  const stop = useCallback(() => {
    const recorder = recorderRef.current
    if (recorder && recorder.state !== 'inactive') recorder.stop()
  }, [])

  const start = useCallback(async () => {
    if (!isVoiceRecordingSupported()) {
      setError('Votre navigateur ne permet pas l’enregistrement vocal, vous pouvez taper votre texte.')
      setStatus('error')
      return
    }

    setError(null)
    setStatus('requesting')

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (e) {
      setError('Micro non autorisé : autorisez l’accès ou tapez votre texte.')
      setStatus('error')
      return
    }

    const mimeType = pickMimeType()
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
    chunksRef.current = []
    recorderRef.current = recorder

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data)
    }

    recorder.onstop = async () => {
      clearTimer()
      stream.getTracks().forEach((track) => track.stop())
      const audio = new Blob(chunksRef.current, { type: recorder.mimeType || mimeType || 'audio/webm' })
      recorderRef.current = null

      if (audio.size < 1000) {
        setError('Nous n’avons rien entendu, réessayez en parlant plus près du micro.')
        setStatus('error')
        return
      }

      setStatus('processing')
      try {
        await onRecorded(audio)
        setStatus('idle')
      } catch (e) {
        const message = (e as { response?: { data?: { message?: string } } })?.response?.data?.message
        setError(message || 'La transcription a échoué, vous pouvez taper votre texte.')
        setStatus('error')
      }
    }

    recorder.start(250)
    startedAtRef.current = Date.now()
    setElapsedSeconds(0)
    setStatus('recording')

    timerRef.current = window.setInterval(() => {
      const elapsed = Math.round((Date.now() - startedAtRef.current) / 1000)
      setElapsedSeconds(elapsed)
      if (elapsed >= maxDurationSeconds) stop()
    }, 250)
  }, [maxDurationSeconds, onRecorded, stop])

  useEffect(() => () => {
    clearTimer()
    const recorder = recorderRef.current
    if (recorder && recorder.state !== 'inactive') {
      recorder.onstop = null
      recorder.stop()
      recorder.stream.getTracks().forEach((track) => track.stop())
    }
  }, [])

  return {
    status,
    elapsedSeconds,
    remainingSeconds: Math.max(maxDurationSeconds - elapsedSeconds, 0),
    error,
    start,
    stop,
    isSupported: isVoiceRecordingSupported(),
  }
}
